import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import type { WebStorage } from "redux-persist/lib/types";

import alertDialog from "./slices/alertDialogSlice";
import allStaff from "./slices/allStaffSlice";
import auth from "./slices/authSlice";
import cart from "./slices/cartSlice";
import closeDates from "./slices/closeDatesSlice";
import common from "./slices/commonSlice";
import dinein from "./slices/dineinSlice";
import giftCards from "./slices/giftCardsSlice";
import history from "./slices/historySlice";
import instructions from "./slices/instructionsSlice";
import menu from "./slices/menuSlice";
import offers from "./slices/offersSlice";
import offlineOrders from "./slices/offlineOrdersSlice";
import operationalRoles from "./slices/operationalRolesSlice";
import ratings from "./slices/ratingsSlice";
import settings from "./slices/settingsSlice";
import shifts from "./slices/shiftsSlice";
import snackbar from "./slices/snackbarSlice";
import staffOrders from "./slices/staffOrdersSlice";
import vouchers from "./slices/vouchersSlice";
import xReport from "./slices/xReportSlice";

/**
 * `localStorage` does not exist while rendering on the server, so fall back to
 * a no-op storage engine there. Without this redux-persist warns on every SSR
 * pass and the first client render can mismatch.
 */
const createNoopStorage = (): WebStorage => ({
  getItem: () => Promise.resolve(null),
  setItem: () => Promise.resolve(),
  removeItem: () => Promise.resolve(),
});

const storage: WebStorage =
  typeof window !== "undefined"
    ? createWebStorage("local")
    : createNoopStorage();

const rootReducer = combineReducers({
  alertDialog,
  // The order-taker roster and the X Report are BOTH session data and neither
  // is persisted: a roster restored from disk would offer an operator who has
  // since left, and a restored report would open on yesterday's takings.
  allStaff,
  auth,
  cart,
  closeDates,
  common,
  dinein,
  giftCards,
  history,
  instructions,
  menu,
  offers,
  // The offline queue's UI projection. Deliberately NOT in the persist
  // whitelist below — its durable home is IndexedDB plus the localStorage
  // backup (`src/lib/offlineStore.ts`), and two stores that can disagree is
  // how a queued order gets lost.
  offlineOrders,
  operationalRoles,
  ratings,
  settings,
  shifts,
  snackbar,
  // The signed-in operator's own OPEN bills, kept current by the live channel
  // (`lib/liveFeed.ts`). Session data, and NOT persisted, for the same reason
  // `allStaff` and `xReport` are not: a list restored from disk would offer a
  // bill the till settled while this tab was closed.
  staffOrders,
  vouchers,
  xReport,
});

/** The un-persisted shape — what selectors actually see. */
export type RootState = ReturnType<typeof rootReducer>;

/**
 * Only the basket, the signed-in customer and the saved item-instruction cache
 * are persisted. Store settings, menu and trading hours are re-fetched on every
 * page load so a stale price or a closed store can never be served from cache.
 *
 * `offers` was on this list and has been REMOVED: the offer, voucher and
 * gift-card catalogues are session data in the original (socket-fed, never
 * persisted), and a stale redemption count would let the wallet offer a claim
 * the server has already spent. The claims themselves survive a reload because
 * they live on `cart.redeemed_offers`, and the cart IS persisted.
 *
 * `dinein` IS persisted, as the original persists it. The dine-in floor plan is
 * fetched by `/menu/dine-in` and by nothing else — `/review-order` reads it to
 * name the chosen table and never asks for it — so a hard reload of the
 * checkout would otherwise show "Select Table" over a basket that has one.
 */
const persistConfig = {
  key: "eposmatic-web",
  version: 1,
  storage,
  // Rapid +/− taps previously serialized the complete persisted cart and auth
  // state to synchronous localStorage after every click. Coalesce that disk
  // work while keeping persistence near-immediate and flush-safe.
  throttle: 150,
  whitelist: ["cart", "auth", "instructions", "dinein"],
};

export const makeStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: persistReducer(persistConfig, rootReducer),
    preloadedState: preloadedState as never,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  });

export const store = makeStore();
export const persistor = persistStore(store);

export type AppStore = typeof store;
export type AppDispatch = AppStore["dispatch"];

/** Typed aliases so components don't import react-redux directly. */
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
